import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Search, Loader2, Swords, Users, MapPin, Shield, GraduationCap, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SearchResults {
  matches: any[];
  players: any[];
  courts: any[];
  clubs: any[];
  classes: any[];
}

const DEBOUNCE_MS = 300;
const MAX_RESULTS = 5;

export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResults>({ matches: [], players: [], courts: [], clubs: [], classes: [] });
  const navigate = useNavigate();

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Search
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults({ matches: [], players: [], courts: [], clubs: [], classes: [] });
      return;
    }

    let cancelled = false;
    const doSearch = async () => {
      setLoading(true);
      const q = `%${debouncedQuery}%`;

      // Check if searching by short_code (e.g. #SR-XXXX or SR-XXXX)
      const codeQuery = debouncedQuery.replace(/^#/, '');

      const [matchesRes, playersRes, courtsRes, clubsRes, classesRes] = await Promise.all([
        supabase
          .from('matches')
          .select('id, title, location, sport, date, time, status, short_code')
          .or(`title.ilike.${q},location.ilike.${q},short_code.ilike.%${codeQuery}%`)
          .order('date', { ascending: false })
          .limit(MAX_RESULTS),
        supabase
          .from('profiles')
          .select('id, name, avatar, player_id, rank, sport')
          .or(`name.ilike.${q},player_id.ilike.%${codeQuery}%`)
          .limit(MAX_RESULTS),
        supabase
          .from('court_slots')
          .select('id, court_name, court_id, location, sport, start_time, end_time, day_of_week')
          .or(`court_name.ilike.${q},location.ilike.${q}`)
          .limit(MAX_RESULTS),
        supabase
          .from('clubs')
          .select('id, name, sport, member_count, rank, avatar, location')
          .ilike('name', q)
          .limit(MAX_RESULTS),
        supabase
          .from('training_classes')
          .select('id, title, sport, skill_level, fee, status')
          .ilike('title', q)
          .eq('status', 'active')
          .limit(MAX_RESULTS),
      ]);

      if (cancelled) return;

      setResults({
        matches: matchesRes.data || [],
        players: playersRes.data || [],
        courts: courtsRes.data || [],
        clubs: clubsRes.data || [],
        classes: classesRes.data || [],
      });
      setLoading(false);
    };

    doSearch();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const goTo = useCallback((path: string) => {
    onOpenChange(false);
    setQuery('');
    navigate(path);
  }, [navigate, onOpenChange]);

  const totalResults = results.matches.length + results.players.length + results.courts.length + results.clubs.length + results.classes.length;
  const hasQuery = debouncedQuery.length >= 2;

  const sportLabel: Record<string, string> = {
    badminton: 'Cầu lông', football: 'Bóng đá', tennis: 'Tennis',
    basketball: 'Bóng rổ', pickleball: 'Pickleball', table_tennis: 'Bóng bàn',
  };

  const statusLabel: Record<string, string> = {
    upcoming: 'Sắp diễn ra', in_progress: 'Đang diễn ra', completed: 'Đã kết thúc', cancelled: 'Đã hủy',
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQuery(''); }}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden max-h-[85vh]">
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="w-5 h-5 text-muted-foreground shrink-0" />
          <Input
            placeholder="Tìm trận đấu, người chơi, sân, CLB..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 focus-visible:border-transparent px-0 h-9 bg-transparent"
            autoFocus
          />
          {query && (
            <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" onClick={() => setQuery('')}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-2">
            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Đang tìm kiếm...</span>
              </div>
            )}

            {/* No results */}
            {!loading && hasQuery && totalResults === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Không tìm thấy kết quả cho "{debouncedQuery}"
              </div>
            )}

            {/* Hint */}
            {!hasQuery && !loading && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Nhập ít nhất 2 ký tự để tìm kiếm
              </div>
            )}

            {/* Matches */}
            {results.matches.length > 0 && (
              <ResultSection
                icon={<Swords className="w-4 h-4" />}
                title="Trận đấu"
                onViewAll={() => goTo(`/matches?search=${encodeURIComponent(debouncedQuery)}`)}
              >
                {results.matches.map((m) => (
                  <ResultItem key={m.id} onClick={() => goTo(`/matches/${m.id}`)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{m.title || 'Trận đấu'}</p>
                      <p className="text-xs text-muted-foreground truncate">{m.location}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {m.short_code && (
                        <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          #{m.short_code}
                        </span>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        {statusLabel[m.status] || m.status}
                      </Badge>
                    </div>
                  </ResultItem>
                ))}
              </ResultSection>
            )}

            {/* Players */}
            {results.players.length > 0 && (
              <ResultSection
                icon={<Users className="w-4 h-4" />}
                title="Người chơi"
                onViewAll={() => goTo(`/players?search=${encodeURIComponent(debouncedQuery)}`)}
              >
                {results.players.map((p) => (
                  <ResultItem key={p.id} onClick={() => goTo(`/players/${p.id}`)}>
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0 overflow-hidden">
                      {p.avatar ? (
                        <img src={p.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-4 h-4 text-accent" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.player_id && `#${p.player_id}`} · {sportLabel[p.sport] || p.sport}</p>
                    </div>
                  </ResultItem>
                ))}
              </ResultSection>
            )}

            {/* Courts */}
            {results.courts.length > 0 && (
              <ResultSection
                icon={<MapPin className="w-4 h-4" />}
                title="Sân"
                onViewAll={() => goTo(`/courts?search=${encodeURIComponent(debouncedQuery)}`)}
              >
                {results.courts.map((c) => (
                  <ResultItem key={c.id} onClick={() => goTo(`/courts/${c.court_id}`)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.court_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{c.location}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {sportLabel[c.sport] || c.sport}
                    </Badge>
                  </ResultItem>
                ))}
              </ResultSection>
            )}

            {/* Clubs */}
            {results.clubs.length > 0 && (
              <ResultSection
                icon={<Shield className="w-4 h-4" />}
                title="Câu lạc bộ"
                onViewAll={() => goTo(`/clubs?search=${encodeURIComponent(debouncedQuery)}`)}
              >
                {results.clubs.map((c) => (
                  <ResultItem key={c.id} onClick={() => goTo(`/clubs/${c.id}`)}>
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0 overflow-hidden">
                      {c.avatar ? (
                        <img src={c.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Shield className="w-4 h-4 text-accent" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.member_count} thành viên · {sportLabel[c.sport] || c.sport}</p>
                    </div>
                  </ResultItem>
                ))}
              </ResultSection>
            )}

            {/* Classes */}
            {results.classes.length > 0 && (
              <ResultSection
                icon={<GraduationCap className="w-4 h-4" />}
                title="Lớp học"
                onViewAll={() => goTo(`/classes?search=${encodeURIComponent(debouncedQuery)}`)}
              >
                {results.classes.map((c) => (
                  <ResultItem key={c.id} onClick={() => goTo(`/classes/${c.id}`)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {sportLabel[c.sport] || c.sport}
                        {c.fee > 0 && ` · ${c.fee.toLocaleString('vi-VN')}đ`}
                      </p>
                    </div>
                  </ResultItem>
                ))}
              </ResultSection>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function ResultSection({ icon, title, onViewAll, children }: { icon: React.ReactNode; title: string; onViewAll: () => void; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between px-2 py-1.5">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {icon}
          {title}
        </div>
        <button onClick={onViewAll} className="text-xs text-accent hover:underline">
          Xem tất cả
        </button>
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function ResultItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent/10 transition-colors text-left"
    >
      {children}
    </button>
  );
}
